import { isFilled, PrismicImage } from '@cobliteam/prismic';
import { PrismicRichText } from '@cobliteam/prismic/src/components';
import type { BlogPostDocumentDataRelationsItem } from '@cobliteam/prismic/src/types/support-cobli.generated';
import Button from '@cobliteam/ui/components/Button';
import Divider from '@cobliteam/ui/components/Divider';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { motion } from 'framer-motion';

interface RelationDetailProps {
  selectedRelation: BlogPostDocumentDataRelationsItem;
  onBack: () => void;
}

const RelationDetail = ({ selectedRelation, onBack }: RelationDetailProps) => {
  const renderRelationContent = () => {
    if (!selectedRelation) {
      return null;
    }

    const relation = selectedRelation as BlogPostDocumentDataRelationsItem;
    if (!isFilled.contentRelationship(relation.post)) {
      return null;
    }

    const post = relation.post;
    const data = post.data;

    if (!data) return null;

    return (
      <>
        <motion.div className='sticky top-0 mb-6 flex items-center gap-3 border-gray-6 border-b bg-white px-4 py-3'>
          <Button size='sm' color='default' variant='outlined' onClick={onBack} className='mr-1 p-2'>
            <Icon name='chevron-left' size='sm' className='h-4 w-4' />
          </Button>
          <Typography variant='heading-xl'>{data.title}</Typography>
        </motion.div>

        <section className='flex flex-1 flex-col space-y-6 p-4'>
          <div className='max-w-[400px]'>
            <PrismicImage field={data.image} alt={data.title} loading='lazy' />
          </div>
          {data.resume && (
            <div>
              <PrismicRichText field={data.resume} className='text-gray-12' />
            </div>
          )}

          <Divider />

          <div className='space-y-6'>
            {(data as any).slices?.map((slice: any, index: number) => {
              if (!slice?.slice_type) return null;

              return (
                <motion.div key={slice.id || `slice-${index}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + index * 0.1 }}>
                  {slice.slice_type === 'rich_text' && slice.primary?.richtext && (
                    <div className='prose prose-sm max-w-none space-y-4 text-gray-12 [&>em]:text-gray-11 [&>em]:italic [&>h1]:mt-6 [&>h1]:mb-4 [&>h1]:text-gray-12 [&>h2]:mt-6 [&>h2]:mb-4 [&>h2]:text-gray-12 [&>h3]:mt-5 [&>h3]:mb-3 [&>h3]:text-gray-12 [&>li]:pl-2 [&>li]:text-gray-11 [&>li]:leading-relaxed [&>ol>li]:ml-6 [&>ol>li]:list-decimal [&>ol>li]:marker:text-primary-9 [&>ol]:my-6 [&>ol]:space-y-3 [&>p]:mb-4 [&>p]:text-gray-11 [&>p]:leading-relaxed [&>strong]:font-semibold [&>strong]:text-gray-12 [&>ul>li]:ml-6 [&>ul>li]:list-disc [&>ul>li]:marker:text-primary-9 [&>ul]:my-6 [&>ul]:space-y-3 [&_em]:text-gray-11 [&_em]:italic [&_strong]:font-semibold [&_strong]:text-gray-12'>
                      <PrismicRichText field={slice.primary.richtext} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>
      </>
    );
  };

  return (
    <motion.div className='flex flex-1 flex-col' initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
      {renderRelationContent()}
    </motion.div>
  );
};

export default RelationDetail;
